import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { ApartmentOutlined, ErrorOutlineOutlined, TaskAltOutlined, UpdateOutlined } from "@mui/icons-material";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { lwfSiteHeader } from "pages/PayRoll/PayRollHeaders";
import { Field } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid, StatutoryList } from "pages/PayRoll/Shared/statutoryUi";
import { formatDay } from "./lwfConfigData";

// LWF Sites (LWF1): every work location with its current LWF version, from
// GET config/sites/ (rows mapped in lwfConfigApi.js). No employee counts or
// amounts here (AC-L01); those are on Employees and Reports.

const ALL = "all";
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };

// `unlisted`: active work locations LWF doesn't list yet (no employees and
// not set up); they can be set up too.
// No site set up yet: only the "LWF is not set up yet." banner with Set up
// LWF (same as ESI and PF); the table shows once a site is saved.
// addRequest: bumped by the page header's "Set up site" button (top right,
// like the Team Workspace Approve screens).
export const LwfSitesList = ({ rows: allRows, cards: counts, unlisted = [], onOpenSite, onSetUp, addRequest = 0 }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [pickOpen, setPickOpen] = useState(false);
  const [pickId, setPickId] = useState("");

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "lwf_sites_saved_views",
    fullHeaders: lwfSiteHeader,
  });

  const notSetUp = useMemo(() => allRows.filter((row) => !row.set_up), [allRows]);
  const canSetUp = useMemo(() => [...notSetUp, ...unlisted], [notSetUp, unlisted]);
  const rows =
    card === "setup" ? allRows.filter((row) => row.set_up) : card === "notsetup" ? notSetUp : card === "upcoming" ? allRows.filter((row) => row.upcoming) : allRows;

  const cards = [
    { key: ALL, title: "Sites", value: counts?.sites ?? allRows.length, icon: ApartmentOutlined, tone: "blue" },
    { key: "setup", title: "Set up", value: counts?.set_up ?? allRows.length - notSetUp.length, icon: TaskAltOutlined, tone: "green" },
    { key: "notsetup", title: "Not set up", value: counts?.not_set_up ?? notSetUp.length, icon: ErrorOutlineOutlined, tone: "red" },
    {
      key: "upcoming",
      title: "New version coming",
      value: counts?.new_version_coming ?? allRows.filter((row) => row.upcoming).length,
      icon: UpdateOutlined,
      tone: "amber",
    },
  ];

  const openRow = (row) => (row.set_up ? onOpenSite(row.id) : onSetUp(row.id));

  const startSetUp = () => {
    if (canSetUp.length === 1) {
      onSetUp(canSetUp[0].id);
      return;
    }
    setPickId(canSetUp[0] ? String(canSetUp[0].id) : "");
    setPickOpen(true);
  };

  // Each header click starts a set-up (one site: opens it; more: the picker).
  const seenAddRequest = useRef(addRequest);
  useEffect(() => {
    if (addRequest === seenAddRequest.current) return;
    seenAddRequest.current = addRequest;
    if (canSetUp.length) startSetUp();
    // Only a new click starts it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRequest]);

  const notStarted = !allRows.some((row) => row.set_up);

  const renderCell = (row, key) => {
    switch (key) {
      case "site":
        return (
          <Mui.Box>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{row.site}</Mui.Typography>
            {row.code ? <Mui.Typography sx={designType.captionText}>{row.code}</Mui.Typography> : null}
          </Mui.Box>
        );
      case "version_label":
        return row.set_up ? <Mui.Box component="span" sx={{ fontWeight: designWeights.semibold }}>{row.version_label}</Mui.Box> : "—";
      case "effective_from":
        return row.effective_from ? formatDay(row.effective_from) : "—";
      case "status_label":
        return <ToneChip label={row.status_label} tone={row.status_tone} />;
      case "action":
        return (
          <Mui.Link
            component="button"
            underline="hover"
            onClick={(event) => {
              event.stopPropagation();
              openRow(row);
            }}
            sx={linkSx}
          >
            {row.action}
          </Mui.Link>
        );
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      {notStarted ? (
        <RequestInfoBanner
          title="LWF is not set up yet."
          body={
            canSetUp.length
              ? "LWF is set up for each site. Pick a site and enter its registration number, amounts, deduction months and set dates to start — or mark it Not applicable."
              : "Add a work location in Administration › Sites first. LWF is then set up for each site."
          }
          action={
            canSetUp.length ? (
              <PrimaryButton onClick={startSetUp} sx={{ whiteSpace: "nowrap" }}>
                Set up LWF
              </PrimaryButton>
            ) : null
          }
        />
      ) : (
        <>
          <StatCardGrid cards={cards} value={card} onChange={setCard} />
          <StatutoryList
            title="LWF Sites"
            headers={visibleHeaders}
            titleOverride={titleOverride}
            rows={rows}
            renderCell={renderCell}
            onRowClick={openRow}
            itemLabel="sites"
            searchPlaceholder="Search site"
            metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
          />
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner body="Sites come from Work Locations. Employees pay LWF under the site they work at on the last day of the month. If your company has decided LWF does not apply to a site, mark it Not applicable (with the basis) in its set-up." />
          </Mui.Box>
        </>
      )}

      <GlobalConfirmDialog
        open={pickOpen}
        title="Set up site"
        onCancel={() => setPickOpen(false)}
        onConfirm={() => {
          setPickOpen(false);
          onSetUp(Number(pickId));
        }}
        confirmLabel="Set up"
        cancelLabel="Cancel"
        confirmDisabled={!pickId}
        maxWidth="sm"
      >
        <Field label="Site" helper="Sites with no LWF set-up yet. Set-up opens with empty rules to fill in.">

          <SelectField
            value={pickId}
            onChange={setPickId}
            options={canSetUp.map((row) => ({ value: String(row.id), label: [row.site, row.code, row.state].filter(Boolean).join(" · ") }))}
            disablePortal={false}
          />
        </Field>
      </GlobalConfirmDialog>
    </>
  );
};
